import React, { useState } from "react";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx } from "../../../../components/common/toolbarStyles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useSubjects } from "../../hooks/useSubjects";
import { Subject } from "../../types";
import { useNotify } from "../../../../context/NotificationContext";

const generateCode = (name: string) => name.trim().replace(/\s+/g, "_");

/** `tabs`: when shown inside Academics → Setup, the tabs replace the title in the top bar. */
const SubjectsUI: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const notify = useNotify();
  const { subjects, loading, saving, deleting, error, isDuplicateName, actions } = useSubjects();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Subject | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; code: string } | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, code: string) => {
    setMenuAnchor({ el: event.currentTarget, code });
  };
  const handleMenuClose = () => setMenuAnchor(null);

  const openAdd = () => {
    setEditingSubject(null);
    setName("");
    setDescription("");
    setNameError(null);
    setDialogOpen(true);
  };

  const openEdit = (subject: Subject) => {
    setEditingSubject(subject);
    setName(subject.name);
    setDescription(subject.description);
    setNameError(null);
    setDialogOpen(true);
    handleMenuClose();
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingSubject(null);
    setName("");
    setDescription("");
    setNameError(null);
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Name is required.");
      return;
    }
    if (isDuplicateName(trimmedName, editingSubject?.code)) {
      setNameError("A subject with this name already exists.");
      return;
    }

    const success = editingSubject
      ? await actions.update(editingSubject.code, trimmedName, description)
      : await actions.create(generateCode(trimmedName), trimmedName, description);

    if (success) {
      notify(editingSubject ? "Subject updated successfully." : "Subject created successfully.", "success");
      closeDialog();
    } else {
      notify(editingSubject ? "Failed to update subject." : "Failed to create subject.", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const success = await actions.remove(deleteTarget.code);
    if (success) {
      notify("Subject deleted successfully.", "success");
    } else {
      notify("Failed to delete subject.", "error");
    }
    setDeleteTarget(null);
  };

  return (
    <Box>
      <PageTopBar
        left={tabs ?? "Subjects"}
        right={
          <Button variant="contained" size="small" sx={toolbarButtonSx} startIcon={<AddIcon />} onClick={openAdd}>
            Add Subject
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, pr: 4 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : subjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No subjects found. Create one below.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              subjects.map((subject, index) => (
                <TableRow key={subject.code} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{subject.name}</TableCell>
                  <TableCell>{subject.code}</TableCell>
                  <TableCell>
                    {subject.description || (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 4 }}>
                    <IconButton size="small" onClick={(event) => handleMenuOpen(event, subject.code)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            const subject = subjects.find((s) => s.code === menuAnchor?.code);
            if (subject) openEdit(subject);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            const subject = subjects.find((s) => s.code === menuAnchor?.code);
            if (subject) setDeleteTarget(subject);
            handleMenuClose();
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>
          {editingSubject ? "Edit Subject" : "New Subject"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            label="Name"
            autoFocus
            fullWidth
            size="small"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
            error={Boolean(nameError)}
            helperText={nameError}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Code"
            fullWidth
            size="small"
            value={editingSubject ? editingSubject.code : generateCode(name)}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>Delete Subject</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectsUI;
